import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Button, Tag, Space, Tooltip, message } from 'antd';
import { DollarOutlined, CalendarOutlined, ReloadOutlined } from '@ant-design/icons';
import { paymentService, Payment } from '../../services/paymentService';
import { useTranslation } from 'react-i18next';

interface PaymentsTableProps {
  title?: string;
  pageSize?: number;
  height?: number;
  onPaymentClick?: (payment: Payment) => void;
}

// Couleurs pour status et method
const getStatusColor = (status: string) => {
  switch (status) {
    case 'COMPLETED': 
    case 'COMPLETED_AND_CONSUMED':
      return 'green';
    case 'PENDING':
    case 'REQUESTED':
      return 'orange';
    case 'FAILED':
    case 'ABORTED':
    case 'CANCELED':
    case 'REFUND_FAILED':
    case 'COMPLETED_WITH_ERROR':
      return 'red';
    case 'REFUNDED':
    case 'REFUND_COMPLETED':
      return 'blue';
    default:
      return 'default';
  }
};

const getMethodColor = (method: string) => {
  switch (method) {
    case 'CARD': return 'blue';
    case 'CASH': return 'green';
    case 'TRANSFER': return 'orange';
    default: return 'default';
  }
};

const PaymentsTable: React.FC<PaymentsTableProps> = ({
  title = "Payments",
  pageSize = 10,
  height,
  onPaymentClick
}) => {

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSizeState, setPageSizeState] = useState(pageSize);

  const fetchPayments = useCallback(async (page = 1, size = pageSizeState) => {
    try {
      setLoading(true);
      const response = await paymentService.getAllAgency({ page: page - 1, size }); 
      const data: Payment[] = response.data.content;
      setPayments(data);
      setTotal(response.data.totalElements); 
      setCurrentPage(page);
      setPageSizeState(size);
    } catch (error) {
      message.error(t('payments.loadError'));
    } finally {
      setLoading(false);
    }
  }, [pageSizeState]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleTableChange = (pagination: any) => {
    fetchPayments(pagination.current, pagination.pageSize);
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: '10%', render: (id: number) => `#${id}` },
    { 
      title: t('common.amount'), 
      dataIndex: 'amount', 
      key: 'amount',
      render: (amount: number, record: Payment) => (
        <Space>
          <DollarOutlined style={{ color: '#1890ff' }} />
          <span>{amount} {'€'}</span>
        </Space>
      )
    },
    { 
      title: t('common.date'), 
      dataIndex: 'date', 
      key: 'date',
      render: (date: string) => (
        <Space>
          <CalendarOutlined />
          <span>{new Date(date).toLocaleDateString()}</span>
        </Space>
      )
    },
    { 
      title: t('common.method'), 
      dataIndex: 'method', 
      key: 'method',
      render: (method: string) => <Tag color={getMethodColor(method)}>{method}</Tag>
    },
    { title: t('common.type'), dataIndex: 'type', key: 'type' },
    { 
      title: t('common.status'), 
      dataIndex: 'status', 
      key: 'status',
      render: (status: string) => <Tag color={getStatusColor(status)}>{status}</Tag>
    },
    { 
      title: t('common.description'), 
      dataIndex: 'description', 
      key: 'description',
      ellipsis: true,
      render: (desc: string) => (
        <Tooltip title={desc}><span>{desc || '/'}</span></Tooltip>
      )
    }
  ];

  return (
    <Card 
      title={title}
      extra={
        <Button type="primary" icon={<ReloadOutlined />} onClick={() => fetchPayments(currentPage, pageSizeState)} loading={loading}>
          {t('common.refresh')}
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={payments}
        loading={loading}
        rowKey="id"
        /*pagination={{ 
          current: currentPage,
          pageSize: pageSizeState,
          total,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} paiements`
        }}*/
        pagination={ 
          false
        }
        scroll={{ x: 1200, y: height }}
        onChange={handleTableChange}
        onRow={(record) => ({
          onClick: () => onPaymentClick?.(record),
          style: { cursor: onPaymentClick ? 'pointer' : 'default' }
        })}
        size="middle"
      />
    </Card>
  );
};

export default PaymentsTable;
